import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AgrupadorFecha,
  AvatarIniciales,
  Icon,
  KpiCard,
  KpiGrid,
  EmptyState,
  Tip,
  TextoTruncado,
} from '@/components/ds'
import { inicialesPersona } from '@/lib/iniciales'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { segmentedCount } from '@/components/ui/segmented'
import { Search, MapPin, ChevronLeft, ChevronRight } from '@/components/ds/icons'
import { toast } from 'sonner'
import { useDocumentosBandeja } from '@/hooks/useDocumentosBandeja'
import { api } from '@/lib/api'
import { formatRut } from '@/lib/rut'
import type { DocumentoBandejaItem, TipoDocumento } from '@/types/documento'
import { cn } from '@/lib/utils'
import { formatNombreArea, ordenarAreas } from '@/lib/area'
import { TIPO_DOCUMENTO_CONFIG, TIPOS_DOCUMENTO_ORDEN } from '@/lib/documento-tipo'

// El mapa de tipo → etiqueta + ícono vive en `@/lib/documento-tipo`: el listado
// de Trabajadores rotula la última firma con el MISMO mapa, y dos copias es
// cómo un rename se aplica en una pantalla y se olvida en la otra.
//
// El rediseño había acortado dos labels para los chips ("Capacitación" en vez
// de "Capacitaciones", sin el "(ATS)"), siguiendo el prototipo. Se descarta ese
// cambio: los labels ya son los del módulo compartido y tocarlos acá los
// cambiaría también en Trabajadores. Si se quieren más cortos, es una decisión
// aparte y va en `documento-tipo.ts`, para las dos pantallas a la vez.
const TIPO_CONFIG = TIPO_DOCUMENTO_CONFIG
const TIPOS_ORDEN = TIPOS_DOCUMENTO_ORDEN

/**
 * Huso operativo del sistema. El servidor corre en UTC y cada documento trae su
 * propio `fecha_firma_tz`; este es el fallback y el huso en el que se decide qué
 * día es "hoy" para los encabezados de grupo — el mismo criterio que usa la API
 * para `sincronizados_hoy`.
 */
const TZ_OPERATIVA = 'America/Santiago'

// ─── Utilidades de fecha ──────────────────────────────────────────────────────

const MESES_AB = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC']

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function hoy(): string {
  return isoDate(new Date())
}

/** Partes de fecha/hora de un instante, leídas en un huso concreto. */
function partesEnTz(iso: string, tz: string) {
  const partes = new Intl.DateTimeFormat('es-CL', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso))

  const de = (tipo: Intl.DateTimeFormatPartTypes) => partes.find((p) => p.type === tipo)?.value ?? ''

  return {
    anio: de('year'),
    mes: de('month'),
    dia: de('day'),
    // Con hour12:false, Intl devuelve "24" para la medianoche en varios locales.
    hora: `${de('hour') === '24' ? '00' : de('hour')}:${de('minute')}`,
  }
}

/** `YYYY-MM-DD` de hoy en el huso operativo (no en el del navegador). */
function hoyEnTzOperativa(): string {
  const { anio, mes, dia } = partesEnTz(new Date().toISOString(), TZ_OPERATIVA)
  return `${anio}-${mes}-${dia}`
}

/** Encabezado de grupo: Hoy, Ayer o "25 JUL 2026". */
function etiquetaDeDia(claveDia: string, hoyClave: string): string {
  if (claveDia === hoyClave) return 'Hoy'

  const [a, m, d] = claveDia.split('-')
  // Mediodía UTC para que restar un día no cruce husos por el borde.
  const ayer = new Date(`${hoyClave}T12:00:00Z`)
  ayer.setUTCDate(ayer.getUTCDate() - 1)
  if (claveDia === isoDate(ayer)) return 'Ayer'

  return `${d} ${MESES_AB[Number(m) - 1] ?? m} ${a}`
}

/**
 * "25 may 2026" — la fecha de la fila en la Ficha, donde no hay encabezado de
 * grupo que la ponga. Mes abreviado en minúscula, como el resto de las fechas
 * cortas de la app.
 */
function fechaCorta({ anio, mes, dia }: { anio: string; mes: string; dia: string }): string {
  const mesAb = MESES_AB[Number(mes) - 1]?.toLowerCase() ?? mes
  // Sin cero a la izquierda: "5 may 2026", no "05 may 2026".
  return `${Number(dia)} ${mesAb} ${anio}`
}

/** "hace 6 minutos", "hace 2 horas", "hace 3 días". */
function haceCuanto(iso: string): string {
  const minutos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))

  if (minutos < 1) return 'hace instantes'
  if (minutos < 60) return `hace ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`

  const horas = Math.round(minutos / 60)
  if (horas < 24) return `hace ${horas} ${horas === 1 ? 'hora' : 'horas'}`

  const dias = Math.round(horas / 24)
  return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`
}

// ─── Presets de rango de fecha ────────────────────────────────────────────────

type PresetRango = '7d' | '30d' | '90d' | 'todos'

interface RangoPreset {
  key: PresetRango
  label: string
  desde: string | undefined
  hasta: string | undefined
}

function buildPresets(): RangoPreset[] {
  const hoyStr = hoy()
  const d7 = new Date(); d7.setDate(d7.getDate() - 7)
  const d30 = new Date(); d30.setDate(d30.getDate() - 30)
  const d90 = new Date(); d90.setDate(d90.getDate() - 90)
  return [
    { key: '7d', label: '7 días', desde: isoDate(d7), hasta: hoyStr },
    { key: '30d', label: '30 días', desde: isoDate(d30), hasta: hoyStr },
    { key: '90d', label: '90 días', desde: isoDate(d90), hasta: hoyStr },
    { key: 'todos', label: 'Todo', desde: undefined, hasta: undefined },
  ]
}

// ─── Tipos para la lista de áreas ────────────────────────────────────────────

interface AreaOpcion {
  id: number
  nombre: string
}

// ─── Selección múltiple ──────────────────────────────────────────────────────

/** Clave estable de un documento dentro de la selección. */
function claveDoc(doc: DocumentoBandejaItem): string {
  return `${doc.tipo}:${doc.id}`
}

// ─── Componente principal ─────────────────────────────────────────────────────

const PER_PAGE = 20

export interface BandejaEntradaProps {
  /**
   * Cuando se define, la bandeja queda pre-filtrada y bloqueada a este
   * trabajador (uso: Tab3 · Documentos Terreno de la Ficha del trabajador).
   * Oculta el encabezado, las cifras de cabecera (que son de la empresa
   * completa y en la Ficha darían una lectura falsa) y el buscador. El resto
   * — chips de tipo, rango, área, selección múltiple y descarga — queda igual.
   */
  lockedPersonaUuid?: string
}

export function BandejaEntrada({ lockedPersonaUuid }: BandejaEntradaProps = {}) {
  const standalone = lockedPersonaUuid === undefined

  // Búsqueda
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')

  // Filtro fecha
  const presets = useMemo(() => buildPresets(), [])
  const preset30 = presets.find((p) => p.key === '30d')!
  const [presetActivo, setPresetActivo] = useState<PresetRango>('30d')
  const [desde, setDesde] = useState<string | undefined>(preset30.desde)
  const [hasta, setHasta] = useState<string | undefined>(preset30.hasta)

  // Filtro área
  const [areas, setAreas] = useState<AreaOpcion[]>([])
  const [areaId, setAreaId] = useState<number | undefined>(undefined)

  // Filtro tipo
  const [tipo, setTipo] = useState<TipoDocumento | undefined>(undefined)

  // Paginación
  const [page, setPage] = useState(1)

  // Selección múltiple para la descarga en ZIP
  const [seleccion, setSeleccion] = useState<Record<string, DocumentoBandejaItem>>({})
  const [descargandoZip, setDescargandoZip] = useState(false)

  // Debounce búsqueda
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // Cargar áreas de la empresa (una sola vez)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get<{ data: Array<{ id: number; nombre: string }> }>('/areas?all=1')
        if (cancelled) return
        setAreas(ordenarAreas(res.data.map((a) => ({ id: a.id, nombre: a.nombre }))))
      } catch {
        // No crítico — el filtro queda sin opciones
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const params = useMemo(
    () => ({
      q: q || undefined,
      tipo,
      persona_uuid: lockedPersonaUuid,
      area_id: areaId,
      desde,
      hasta,
      page,
      per_page: PER_PAGE,
    }),
    [q, tipo, lockedPersonaUuid, areaId, desde, hasta, page],
  )

  const { data, loading, error, counts, resumen } = useDocumentosBandeja(params)
  // Memoizado porque el `?? []` crea un array nuevo en cada render y la
  // agrupación por día lo tiene como dependencia.
  const items = useMemo(() => data?.data ?? [], [data])
  const meta = data?.meta

  // ── Helpers de filtros ────────────────────────────────────────────────────

  function seleccionarPreset(key: PresetRango) {
    const p = presets.find((x) => x.key === key)
    if (!p) return
    setPresetActivo(p.key)
    setDesde(p.desde)
    setHasta(p.hasta)
    setPage(1)
  }

  function seleccionarArea(id: number | undefined) {
    setAreaId(id)
    setPage(1)
  }

  function seleccionarTipo(t: TipoDocumento | undefined) {
    setTipo(t)
    setPage(1)
  }

  // ── Selección ─────────────────────────────────────────────────────────────

  const seleccionados = Object.values(seleccion)

  function alternarSeleccion(doc: DocumentoBandejaItem) {
    setSeleccion((prev) => {
      const clave = claveDoc(doc)
      if (prev[clave]) {
        const resto = { ...prev }
        delete resto[clave]
        return resto
      }
      return { ...prev, [clave]: doc }
    })
  }

  async function descargarSeleccion() {
    setDescargandoZip(true)
    try {
      const { blob, filename } = await api.blobPost('/documentos/zip', {
        documentos: seleccionados.map((d) => ({ tipo: d.tipo, id: d.id })),
      })
      // `filename` viene del Content-Disposition, que en cross-origin el
      // navegador puede no exponer (ver `exposed_headers` en cors.php): el
      // fallback replica el nombre que arma el servidor.
      descargarBlob(blob, filename ?? `documentos_firmados_${hoyEnTzOperativa()}.zip`)
      setSeleccion({})
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo descargar la selección')
    } finally {
      setDescargandoZip(false)
    }
  }

  // ── Agrupación por día ────────────────────────────────────────────────────

  const hoyClave = useMemo(() => hoyEnTzOperativa(), [])

  const grupos = useMemo(() => {
    const porDia: Array<{ clave: string; label: string; items: DocumentoBandejaItem[] }> = []

    for (const doc of items) {
      // El día se calcula en el huso PROPIO del documento, el mismo con el que
      // se muestra la hora de la fila. Sin fecha de firma no hay día al que
      // pertenecer: cae en su propio grupo en vez de mentir asignándole hoy.
      let clave = 'sin-fecha'
      if (doc.fecha_firma) {
        const { anio, mes, dia } = partesEnTz(doc.fecha_firma, doc.fecha_firma_tz ?? TZ_OPERATIVA)
        clave = `${anio}-${mes}-${dia}`
      }

      let grupo = porDia.find((g) => g.clave === clave)
      if (!grupo) {
        grupo = {
          clave,
          label: clave === 'sin-fecha' ? 'Sin fecha de firma' : etiquetaDeDia(clave, hoyClave),
          items: [],
        }
        porDia.push(grupo)
      }
      grupo.items.push(doc)
    }

    return porDia
  }, [items, hoyClave])

  // ─────────────────────────────────────────────────────────────────────────

  const areaSeleccionada = areas.find((a) => a.id === areaId)
  const sinNadaSincronizado = resumen.ultima_sincronizacion === null

  return (
    <div className="space-y-4">
      {/* Cabecera — sólo en el uso standalone; en la Ficha el tab provee su propio encabezado */}
      {standalone && (
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-60 flex-1">
            <h1 className="text-3xl font-bold tracking-tight">Bandeja de Entrada</h1>
            <p className="text-muted-foreground mt-1 text-[13.5px]">
              Documentos firmados en la app móvil que ya sincronizaron a esta empresa.
            </p>
          </div>
          <span className="text-meta inline-flex flex-none items-center gap-1.5 text-[11.5px] whitespace-nowrap">
            <Icon name="cloud_done" size={15} className="text-primary" />
            {resumen.ultima_sincronizacion
              ? `Última sincronización ${haceCuanto(resumen.ultima_sincronizacion)}`
              : 'Sin sincronizaciones todavía'}
          </span>
        </div>
      )}

      {/* Cifras de cabecera — de la empresa completa, así que no van en la Ficha */}
      {standalone && (
        <KpiGrid min={196}>
          <KpiCard
            label="Sincronizados hoy"
            valor={resumen.sincronizados_hoy}
            detalle={resumen.sincronizados_hoy > 0 ? 'desde la app móvil' : 'sin actividad todavía'}
          />
          <KpiCard label="Últimos 7 días" valor={resumen.firmados_7d} detalle="firmados en terreno" />
          <KpiCard
            label="Trabajadores con registros"
            valor={`${resumen.trabajadores_con_registros}/${resumen.trabajadores_total}`}
            detalle="en el rango elegido"
          />
        </KpiGrid>
      )}

      {/* Tarjeta del DS: superficie blanca sobre el lienzo gris, radio 16 y
          bordes recortados por `overflow-hidden` (mismo contenedor que `DataTable`). */}
      <div className="bg-card overflow-hidden rounded-2xl border">
        {/* Barra de filtros */}
        <div className="border-border-subtle flex flex-col gap-2.5 border-b px-3.5 py-3">
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Buscar — sin sentido cuando la bandeja está fijada a un trabajador (Ficha) */}
            {standalone && (
              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por trabajador, RUT o tipo…"
                  className="h-8 pl-8 text-[13px]"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            )}

            <ToggleGroup
              type="single"
              variant="segmented"
              value={presetActivo}
              // Radix permite deseleccionar en modo single: sin este guard, un
              // segundo click sobre el rango activo dejaría la bandeja sin rango.
              onValueChange={(v) => v && seleccionarPreset(v as PresetRango)}
              aria-label="Rango de fechas"
              className="flex-none"
            >
              {presets.map((p) => (
                <ToggleGroupItem key={p.key} value={p.key}>
                  {p.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            <NativeSelect
              size="sm"
              className="h-8 flex-none"
              aria-label="Filtrar por área"
              value={areaId === undefined ? '' : String(areaId)}
              onChange={(e) =>
                seleccionarArea(e.target.value === '' ? undefined : Number(e.target.value))
              }
            >
              <NativeSelectOption value="">Todas las áreas</NativeSelectOption>
              {areas.map((a) => (
                <NativeSelectOption key={a.id} value={String(a.id)}>
                  {formatNombreArea(a)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>

          {/* Chips de tipo */}
          <ToggleGroup
            type="single"
            variant="segmented"
            value={tipo ?? 'todos'}
            onValueChange={(v) =>
              v && seleccionarTipo(v === 'todos' ? undefined : (v as TipoDocumento))
            }
            aria-label="Tipo de documento"
            // `justify-start` va acá y NO en `segmented.ts`: el riel del DS
            // centra sus ítems, que es lo correcto en una fila sola. Con seis
            // chips que envuelven en pantalla angosta, centrar deja cada línea
            // suelta al medio y la lista se lee como un poema.
            className="max-w-full flex-wrap justify-start"
          >
            <ToggleGroupItem value="todos">
              <Icon name="inbox" size={15} />
              Todos
              <span className={segmentedCount}>{counts.total}</span>
            </ToggleGroupItem>
            {TIPOS_ORDEN.map((t) => {
              const cfg = TIPO_CONFIG[t]
              return (
                <ToggleGroupItem key={t} value={t}>
                  <Icon name={cfg.icon} size={15} />
                  {cfg.label}
                  <span className={segmentedCount}>{counts[t]}</span>
                </ToggleGroupItem>
              )
            })}
          </ToggleGroup>
        </div>

        {/* Barra de selección */}
        {seleccionados.length > 0 && (
          <div className="bg-primary-50 border-primary-200 flex flex-wrap items-center gap-2.5 border-b px-3.5 py-2.5">
            <span className="text-primary-strong min-w-40 flex-1 text-[12.5px] font-medium">
              {seleccionados.length}{' '}
              {seleccionados.length === 1 ? 'documento seleccionado' : 'documentos seleccionados'}
            </span>
            <Button size="xs" disabled={descargandoZip} onClick={descargarSeleccion}>
              <Icon name="download" size={15} />
              {descargandoZip ? 'Preparando ZIP…' : 'Descargar ZIP'}
            </Button>
            <Button variant="ghost" size="xs" onClick={() => setSeleccion({})}>
              Quitar selección
            </Button>
          </div>
        )}

        {loading && (
          <p className="text-muted-foreground px-3.5 py-8 text-center text-sm">Cargando…</p>
        )}
        {error && <p className="text-destructive px-3.5 py-8 text-center text-sm">{error}</p>}

        {!loading && !error && items.length === 0 && (
          <EmptyState
            icono={sinNadaSincronizado ? 'inbox' : 'search_off'}
            mensaje={
              sinNadaSincronizado
                ? 'La bandeja se llena sola: cuando un supervisor registra una entrega de EPP, una capacitación, un ATS o un checklist en la app móvil y el trabajador firma, el documento aparece acá.'
                : 'Ningún documento firmado coincide con estos filtros.'
            }
          />
        )}

        {!loading &&
          !error &&
          grupos.map((grupo) => (
            <div key={grupo.clave}>
              {/* La banda de día ordena una bandeja con decenas de documentos por
                  jornada. En la Ficha el mismo criterio produce una banda por
                  documento —un trabajador firma cosas sueltas a lo largo de
                  meses—, así que ahí la fecha va en la fila. */}
              {/* Sin el badge NUEVO del DS a propósito: la bandeja no lleva
                  seguimiento de qué documentos ya se miraron. Un documento
                  firmado es evidencia, no una notificación por atender. */}
              {standalone && (
                <AgrupadorFecha label={grupo.label} cuenta={grupo.items.length} />
              )}
              {grupo.items.map((doc) => (
                <DocumentoFila
                  key={claveDoc(doc)}
                  doc={doc}
                  marcado={seleccion[claveDoc(doc)] !== undefined}
                  onToggle={() => alternarSeleccion(doc)}
                  enFicha={!standalone}
                />
              ))}
            </div>
          ))}

        {/* Pie: cuenta + paginación */}
        {meta && (
          <div className="bg-surface-muted flex items-center gap-2.5 px-3.5 py-2.5">
            <span className="text-muted-foreground flex-1 text-xs">
              {meta.total} {meta.total === 1 ? 'documento' : 'documentos'}
            </span>
            <span className="fc-num text-meta text-[11.5px]">
              Página {meta.page} de {meta.last_page}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Página anterior"
              disabled={meta.page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Página siguiente"
              disabled={meta.page >= meta.last_page || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </div>

      {/* El área es clasificador opcional, no restrictor: si el filtro está
          puesto y no hay nada, conviene decir por qué antes de que el usuario
          concluya que no hay documentos. */}
      {!loading && !error && items.length === 0 && areaSeleccionada && (
        <p className="text-muted-foreground text-center text-xs">
          Hay un filtro de área activo ({formatNombreArea(areaSeleccionada)}). Los documentos sin
          área asignada no aparecen mientras esté puesto.
        </p>
      )}
    </div>
  )
}

// ─── Descarga ─────────────────────────────────────────────────────────────────

function descargarBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

async function descargarPdf(doc: DocumentoBandejaItem) {
  try {
    const blob = await api.blob(doc.pdf_url)
    const rut = doc.persona?.rut ? doc.persona.rut.replace(/[^0-9Kk]/g, '') : 'sin_rut'
    descargarBlob(blob, `${doc.tipo.toLowerCase()}_${rut}.pdf`)
  } catch (e) {
    toast.error(e instanceof Error ? e.message : 'No se pudo descargar el PDF')
  }
}

// ─── Fila de documento ────────────────────────────────────────────────────────

function DocumentoFila({
  doc,
  marcado,
  onToggle,
  enFicha,
}: {
  doc: DocumentoBandejaItem
  marcado: boolean
  onToggle: () => void
  /**
   * La fila vive dentro de la Ficha del trabajador, no en la bandeja.
   *
   * Cambia tres cosas, y las tres por el mismo motivo: en la Ficha ya se sabe
   * de quién es el documento, y la lista no está agrupada por día.
   *   1. No se repite el trabajador (nombre, RUT y avatar).
   *   2. La fecha va en la fila — en la bandeja la pone el encabezado del grupo.
   *   3. El área baja bajo el nombre del documento, porque su lugar de antes
   *      era la línea del RUT, que acá no existe.
   */
  enFicha: boolean
}) {
  const cfg = TIPO_CONFIG[doc.tipo]
  const partes = doc.fecha_firma
    ? partesEnTz(doc.fecha_firma, doc.fecha_firma_tz ?? TZ_OPERATIVA)
    : null
  const hora = partes?.hora ?? '—'
  const fecha = partes ? fechaCorta(partes) : null

  const trabajador = doc.persona?.nombre ?? 'Sin trabajador'
  const rutTrabajador = doc.persona?.rut ? formatRut(doc.persona.rut) : null
  const area = doc.area ? formatNombreArea(doc.area) : 'Sin área'

  // El prototipo muestra un "lugar" bajo la hora. El dato real equivalente es
  // el contrato: en este dominio es la faena / proyecto / servicio donde se
  // firmó, o sea el DÓNDE.
  const lugar = doc.contrato?.nombre_referencia?.trim() || null

  // Segunda línea. En la bandeja alcanza el folio: el área viaja en la línea
  // del trabajador y el contrato, bajo la hora. En la Ficha esas dos líneas
  // desaparecen, así que los datos se juntan acá en vez de perderse.
  const subtitulo = enFicha
    ? [doc.folio, lugar, doc.area ? area : null].filter(Boolean).join(' · ')
    : doc.folio

  function abrirMapa() {
    if (doc.tiene_geolocalizacion && doc.latitud !== null && doc.longitud !== null) {
      window.open(`https://www.google.com/maps?q=${doc.latitud},${doc.longitud}`, '_blank')
    }
  }

  return (
    <div
      className={cn(
        // Divisor #F6F6F7 (no el borde de tarjeta, que es más oscuro y raya la
        // lista) y hover apenas perceptible.
        'border-border-faint hover:bg-primary-50 flex flex-wrap items-center gap-2.5 border-b px-3.5 py-2.5 transition-colors',
        marcado && 'bg-primary-50',
      )}
    >
      <Checkbox
        checked={marcado}
        onCheckedChange={onToggle}
        aria-label={`Seleccionar ${cfg.label} de ${trabajador}`}
      />

      {/* Ícono del tipo */}
      <div className="bg-muted flex size-7 flex-none items-center justify-center rounded-lg">
        <Icon name={cfg.icon} size={16} className="text-body" />
      </div>

      {/* Tipo + folio */}
      <div className="flex min-w-[150px] flex-[1_1_190px] flex-col gap-0.5">
        <TextoTruncado className="text-[13px] font-medium">{cfg.label}</TextoTruncado>
        <Tip texto={enFicha ? `Folio ${doc.folio}` : 'Folio del documento firmado'}>
          <span className={cn('text-soft truncate text-[10.5px]', !enFicha && 'fc-num')}>
            {subtitulo}
          </span>
        </Tip>
      </div>

      {/* Trabajador — en la Ficha sobra: la pantalla entera ya es de esta persona */}
      {!enFicha && (
        <div className="flex min-w-0 flex-[1_1_200px] items-center gap-2.5">
          <AvatarIniciales iniciales={inicialesPersona(trabajador)} />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[12.5px]">{trabajador}</span>
            <span className="text-soft truncate text-[10.5px]">
              {rutTrabajador ? `${rutTrabajador} · ${area}` : area}
            </span>
          </div>
        </div>
      )}

      {/* Geolocalización */}
      <Tip
        claseEnvoltorio="flex-none"
        texto={
          doc.tiene_geolocalizacion
            ? `Ver en mapa (${doc.latitud}, ${doc.longitud})`
            : 'Firmado sin geolocalización'
        }
      >
        <button
          type="button"
          aria-label={
            doc.tiene_geolocalizacion ? 'Ver en mapa' : 'Firmado sin geolocalización'
          }
          disabled={!doc.tiene_geolocalizacion}
          onClick={abrirMapa}
          className={cn(
            'flex-none rounded p-0.5 transition-colors',
            doc.tiene_geolocalizacion
              ? 'text-primary-hover hover:bg-primary-100 cursor-pointer'
              : 'text-border-strong cursor-default',
          )}
        >
          <MapPin className="size-4" />
        </button>
      </Tip>

      {/* Cuándo. En la bandeja el día lo pone el encabezado del grupo y acá va
          la hora con el contrato debajo; en la Ficha no hay grupos, así que la
          fecha tiene que estar en la fila o no está en ninguna parte. */}
      <div className="flex min-w-[74px] flex-none flex-col items-end gap-px">
        {enFicha ? (
          <>
            <span className="text-strong text-[12px]">{fecha ?? 'Sin fecha'}</span>
            <span className="fc-num text-soft text-[11px]">{hora}</span>
          </>
        ) : (
          <>
            <span className="fc-num text-strong text-[11.5px]">{hora}</span>
            {lugar && (
              <TextoTruncado className="text-soft max-w-[140px] text-[10.5px]">
                {lugar}
              </TextoTruncado>
            )}
          </>
        )}
      </div>

      <Tip texto="Descargar el PDF firmado">
        <Button
          variant="outline"
          size="xs"
          className="flex-none"
          onClick={() => descargarPdf(doc)}
        >
          <Icon name="download" size={16} />
          Descargar PDF
        </Button>
      </Tip>
    </div>
  )
}
